import { PickerValidDate } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment, { Moment } from 'moment';
import { useCallback, useMemo } from 'react';

import { ICONS_TYPES } from '../Icon';

import { Sizes } from './enums';
import {
  AttentionIcon,
  ErrorMessage,
  ErrorMessageContainer,
  Label,
  RequiredMark,
  Root,
  StyledDesktopDatePicker,
} from './styles';
import { Props } from './types';

const DatePicker = ({
  className,
  disabled = false,
  error,
  format = 'YYYY-MM-DD',
  fullWidth = false,
  label,
  loading,
  maxDate,
  minDate,
  name,
  onChange,
  popperZIndex = 1210,
  readOnly = false,
  required,
  showLabel,
  size = Sizes.medium,
  value,
  view = ['year', 'month', 'day'],
}: Props) => {
  const disabledOrLoading = useMemo(
    () => disabled || loading,
    [disabled, loading],
  );
  const isClearable = useMemo(
    () => !readOnly && !disabled,
    [disabled, readOnly],
  );

  const handleChange = useCallback(
    (newDate: PickerValidDate | null) => {
      onChange?.(newDate ? moment(newDate as Moment).format(format) : null);
    },
    [format, onChange],
  );

  return (
    <Root className={className}>
      {label && showLabel && (
        <Label htmlFor={name} error={!!error}>
          {required && !disabledOrLoading ? (
            <span>
              {label}
              <RequiredMark error={!!error}>*</RequiredMark>
            </span>
          ) : (
            label
          )}
        </Label>
      )}
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <StyledDesktopDatePicker<string>
          disabled={disabledOrLoading}
          format={format}
          fullWidth={fullWidth}
          label={showLabel ? undefined : label}
          maxDate={maxDate ? moment(maxDate) : undefined}
          minDate={minDate ? moment(minDate) : undefined}
          name={name}
          onChange={handleChange}
          readOnly={readOnly}
          slotProps={{
            field: { clearable: isClearable },
            textField: {
              error: !!error,
              required: required,
            },
            popper: {
              sx: { zIndex: popperZIndex },
            },
          }}
          views={view}
          sizeType={size}
          value={value ? moment(value) : null}
        />
      </LocalizationProvider>
      {!!error && (
        <ErrorMessageContainer sizetype={size}>
          <AttentionIcon icon={ICONS_TYPES.Attention} size={20} />
          <ErrorMessage>{error}</ErrorMessage>
        </ErrorMessageContainer>
      )}
    </Root>
  );
};

export type { Props };
export { Sizes };

export default DatePicker;
